import type { Fraction } from '@mimir/common/util/fractionType';
import type { VideoApiActions } from '@mimir/client-common/util/apiActions';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { PlayableItemDto, PlayableItemGetOptions } from '@mimir/common/dto/playableItemDto';
import type { SegmentProvider, SegmentInfo } from '@mimir/cutter/frameProvider/demuxAndDecode/segmentedPacketProvider';
import type { EncodedData } from '@mimir/common/mpegts/demuxerTypes';
import { addFractions, approximateFractionValue, divideFractions, multiplyFractions } from '@mimir/common/util/integerMath';
import { ItemSegmentUrlCache } from '@mimir/client-common/util/itemUrlCache';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { Logger } from '@mimir/common/types/logger';
import { readWholeStream } from '@mimir/common/util/readableStream';
import { demuxTsSegment } from '@mimir/common/mpegts/mpegTsDemuxer';
import { invariant } from '@mimir/common/util/invariant';
import { decodeFmp4InitSegment, demuxFmp4Segment } from '@mimir/common/mp4/fmp4Demuxer';
import type { Mp4Info } from '@mimir/common/mp4/mp4Types';
import { fetchAndInspectMoov } from '@mimir/common/mp4/mp4Demuxer';
import { buildEncodedDataFromVirtualSegment, findVirtualSegmentIndex } from '@mimir/common/mp4/virtualSegments';
import { fetchByteArrays } from '@mimir/common/mp4/fetchByteArrays';

export function createMimirSegmentProvider(
  services: {
    apiActions: VideoApiActions,
    fetch: BasicFetch,
    logger: Logger,
  },
  itemId: string,
  itemType: 'video' | 'audio',
): SegmentProvider {
  const { apiActions, fetch, logger } = services;

  const options : PlayableItemGetOptions = {
    useEditProxy: true,
    streamSetPreference: [
      [StreamRoleId.LiveRecordWebproxyTs],
      [StreamRoleId.LiveRecordAudioOnlyProxyTs],
      [StreamRoleId.LiveRecordAudioOnlyHighresTs],
      [StreamRoleId.LiveRecordHighresTs],
      [StreamRoleId.EditProxyFull],
    ],
    peakOffset: undefined,
  };

  let item : PlayableItemDto | null = null;
  let itemSegmentCache: ItemSegmentUrlCache | undefined;
  let format: 'ts' | 'fmp4' | 'flatEditProxy' | 'flatWebProxy' | 'flatHighRes' | undefined;
  let mp4Info: Mp4Info | undefined;

  return {
    async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
      if (item === null) {
        item = await apiActions.getPlayableItem(itemId, options);
        if (item === null) {
          throw Error('Failed to get playable item');
        }
      }

      if (item.playableArtifact?.type === 'flatEditProxy'
        // If we have a web proxy for a stereo input that was created without any downmixing,
        // we can use it as a single flat file, so we treat it as an edit proxy
        || (item.playableArtifact?.type === 'flatWebProxy' && item.proxyAudioTrackUrls.length === 2 && item.webProxyAudioDownMixInfo === null)
      ) {
        format = 'flatEditProxy';
        const flatEditProxyUrl = item.playableArtifact.url;
        if (mp4Info === undefined) {
          mp4Info = await fetchAndInspectMoov(fetch, flatEditProxyUrl, undefined, false);
        }

        const virtualSegmentIndex = findVirtualSegmentIndex(position, mp4Info.virtualSegments);
        if (virtualSegmentIndex === -1) {
          return null;
        }
        const virtualSegment = mp4Info.virtualSegments[virtualSegmentIndex];
        return {
          id: `${virtualSegmentIndex}`,
          start: virtualSegment.inclusiveStartInSeconds,
          end: virtualSegment.exclusiveEndInSeconds,
        };
      }

      if (item.playableArtifact?.type === 'flatWebProxy') {
        format = 'flatWebProxy';
        const flatWebProxyUrl = item.playableArtifact.url;
        if (mp4Info === undefined) {
          mp4Info = itemType === 'video' ? await fetchAndInspectMoov(fetch, flatWebProxyUrl, undefined, true) : undefined;
          for (let i = 0; i < item.proxyAudioTrackUrls.length; i++) {
            const proxyAudioTrackUrl = item.proxyAudioTrackUrls[i];
            mp4Info = await fetchAndInspectMoov(fetch, proxyAudioTrackUrl, mp4Info, false);
          }
        }

        if (mp4Info === undefined) {
          throw Error('Item has no media');
        }
        const virtualSegmentIndex = findVirtualSegmentIndex(position, mp4Info.virtualSegments);
        if (virtualSegmentIndex === -1) {
          return null;
        }
        const virtualSegment = mp4Info.virtualSegments[virtualSegmentIndex];
        return {
          id: `${virtualSegmentIndex}`,
          start: virtualSegment.inclusiveStartInSeconds,
          end: virtualSegment.exclusiveEndInSeconds,
        };
      }

      if (item.playableArtifact?.type === 'flatHighRes') {
        format = 'flatHighRes';
        const flatHighresUrl = item.playableArtifact.url;
        if (mp4Info === undefined) {
          mp4Info = itemType === 'video' ? await fetchAndInspectMoov(fetch, flatHighresUrl, undefined, false) : undefined;
        }

        if (mp4Info === undefined) {
          throw Error('Item has no media');
        }

        const virtualSegmentIndex = findVirtualSegmentIndex(position, mp4Info.virtualSegments);
        if (virtualSegmentIndex === -1) {
          return null;
        }
        const virtualSegment = mp4Info.virtualSegments[virtualSegmentIndex];
        return {
          id: `${virtualSegmentIndex}`,
          start: virtualSegment.inclusiveStartInSeconds,
          end: virtualSegment.exclusiveEndInSeconds,
        };
      }

      if (item.media === null) {
        throw Error('Item has no media');
      }

      if (item.playableArtifact?.type !== 'stream' || item.playableArtifact.streamSet.length === 0) {
        throw new Error('Item has unsupported media');
      }

      format = item.playableArtifact.streamSet[0] === StreamRoleId.EditProxyFull ? 'fmp4' : 'ts';
      itemSegmentCache = new ItemSegmentUrlCache(
        apiActions,
        itemId,
        'get',
        item.playableArtifact.streamSet[0],
        10,
      );
      const segmentDuration = item.media.segmentDuration;

      const segmentIndex =
          Math.floor(approximateFractionValue(divideFractions(position, segmentDuration)));

      if ((segmentIndex < 0) || (segmentIndex >= item.media.segmentCount)) {
        return null;
      }

      const segmentIndexAsFraction = { numerator: segmentIndex, denominator: 1 };

      return {
        id: (segmentIndex + (format === 'fmp4' ? 1 : 0)).toString(),
        start: multiplyFractions(segmentIndexAsFraction, segmentDuration),
        end: addFractions(multiplyFractions(segmentIndexAsFraction, segmentDuration), segmentDuration),
      };
    },
    async getSegment(id: string): Promise<EncodedData> {
      const segmentIndex = parseInt(id);
      if (segmentIndex.toString() !== id) {
        throw new Error(`Bad segment id: ${id}`);
      }

      if (format === 'flatEditProxy' || format === 'flatWebProxy' || format === 'flatHighRes') {
        invariant(mp4Info !== undefined, 'Should have fetched mp4 info');
        const virtualSegment = mp4Info.virtualSegments[segmentIndex];
        const byteArrays = await fetchByteArrays(fetch, virtualSegment);
        return buildEncodedDataFromVirtualSegment(byteArrays, virtualSegment, mp4Info);
      }

      invariant(itemSegmentCache !== undefined && format !== undefined, () => `Should have invoked getSegmentInfoAt() because asking for segment ${id}`);
      if (format === 'fmp4' && mp4Info === undefined) {
        const url = await itemSegmentCache.getMediaSegmentUrl(0);
        logger.info('MimirSegmentProvider: fetching init fmp4 segment');
        const response = await fetch(url, { });

        if (!response.ok || (response.body === null)) {
          throw new Error(`Failed to fetch init segment: ${response.status} ${response.statusText}`);
        }
        const body = response.body;
        const fmp4InitSegmentBytes = await readWholeStream(body);
        mp4Info = decodeFmp4InitSegment(fmp4InitSegmentBytes);
      }

      const url = await itemSegmentCache.getMediaSegmentUrl(segmentIndex);

      logger.info(`MimirSegmentProvider: fetching segment: ${segmentIndex}`);

      const response = await fetch(url, { });

      if (!response.ok || (response.body === null)) {
        throw new Error(`Failed to fetch segment: ${response.status} ${response.statusText}`);
      }

      const body = response.body;
      const bytes = await readWholeStream(body);
      return mp4Info === undefined
        ? demuxTsSegment(bytes, false)
        : demuxFmp4Segment(logger, mp4Info, bytes);
    },
    async getDuration(): Promise<Fraction> {
      const item = await apiActions.getPlayableItem(itemId, options);
      if (item === null) {
        throw Error('Failed to get playable item');
      }
      if (!item.playableDuration) {
        throw Error('Item does not have duration');
      }
      return item.playableDuration;
    },
  };
}

